import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import LectureCard from "../components/LectureCard";
import { Filter, X, SlidersHorizontal } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";

const YEAR_RANGES = [
  { key: "2022-2026", label: "2022 – 2026", from: 2022, to: 2026 },
  { key: "2015-2021", label: "2015 – 2021", from: 2015, to: 2021 },
];

const SORT_OPTIONS = [
  { value: "downloads desc", label: "Most Listened" },
  { value: "addeddate desc", label: "Recently Added" },
  { value: "date desc", label: "Newest" },
  { value: "date asc", label: "Oldest" },
  { value: "titleSorter asc", label: "A → Z" },
];

export default function Explore() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const series = params.get("series") || "";
  const yearRangeKey = params.get("yr") || "";
  const scholar = params.get("scholar") || "";
  const sort = params.get("sort") || "downloads desc";

  const [results, setResults] = useState(null);
  const [scholars, setScholars] = useState([]);
  const [manifestSeries, setManifestSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const yearRange = YEAR_RANGES.find(r => r.key === yearRangeKey) || null;

  const updateParam = (key, value) => {
    const p = new URLSearchParams(params);
    if (value === null || value === undefined || value === "") p.delete(key);
    else p.set(key, value);
    setParams(p, { replace: true });
    setPage(1);
  };

  const clearAll = () => {
    setParams({}, { replace: true });
    setPage(1);
  };

  useEffect(() => {
    // fetch scholars + live series once for the filter panel
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scholars`)
      .then(r => r.json())
      .then(d => setScholars(d.scholars || []))
      .catch(() => setScholars([]));
    api.manifest()
      .then(d => setManifestSeries((d.series || []).filter(s => s.status === "live")))
      .catch(() => setManifestSeries([]));
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.search({
      q, series: series || undefined,
      year_from: yearRange?.from,
      year_to: yearRange?.to,
      creator: scholar || undefined,
      sort,
      page,
      rows: 24,
    }).then(d => { if (alive) setResults(d); })
      .catch(() => alive && setResults({ items: [], total: 0 }))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [q, series, yearRangeKey, scholar, sort, page]);

  const activeChips = useMemo(() => {
    const chips = [];
    if (q) chips.push({ key: "q", label: `"${q}"` });
    if (series) chips.push({ key: "series", label: `Series: ${series}` });
    if (yearRange) chips.push({ key: "yr", label: `Year: ${yearRange.label}` });
    if (scholar) chips.push({ key: "scholar", label: `Scholar: ${scholar}` });
    return chips;
  }, [q, series, yearRange, scholar]);

  return (
    <div className="animate-fade-up" data-testid="page-explore">
      {/* header */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-caps text-gold/85">The Archive</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-paper mt-2">Search Results</h1>
          <p className="text-white/60 mt-2 text-sm sm:text-base">
            {loading ? "Searching…" : `Showing ${results?.items?.length || 0} of ${results?.total?.toLocaleString?.() || 0} lectures`}
            {q && <> for <span className="italic text-paper">&ldquo;{q}&rdquo;</span></>}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setMobileFiltersOpen(true)}
            className="lg:hidden chip"
            data-testid="mobile-filters-btn"
          >
            <SlidersHorizontal className="w-3.5 h-3.5" /> Filters {activeChips.length > 0 && `(${activeChips.length})`}
          </button>
          {activeChips.map(c => (
            <button
              key={c.key}
              onClick={() => updateParam(c.key, null)}
              className="chip active"
              data-testid={`active-chip-${c.key}`}
            >
              {c.label} <X className="w-3.5 h-3.5" />
            </button>
          ))}
          {activeChips.length > 0 && (
            <button onClick={clearAll} className="text-xs text-white/50 hover:text-white/80 underline" data-testid="clear-filters">Clear all</button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-8">
        {/* filter panel — desktop only; mobile uses drawer */}
        <aside className="archival-card rounded-lg p-6 h-fit lg:sticky lg:top-24 hidden lg:block" data-testid="filter-panel">
          <FilterPanelBody
            sort={sort}
            yearRangeKey={yearRangeKey}
            manifestSeries={manifestSeries}
            series={series}
            scholars={scholars}
            scholar={scholar}
            updateParam={updateParam}
          />
        </aside>

        <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
          <SheetContent side="left" className="w-[92%] sm:max-w-md bg-[#110d0a] border-r border-white/5 text-paper overflow-y-auto">
            <SheetHeader className="mb-4">
              <SheetTitle className="font-display text-2xl text-paper flex items-center gap-2">
                <Filter className="w-5 h-5 text-gold" /> Filters
              </SheetTitle>
            </SheetHeader>
            <FilterPanelBody
              sort={sort}
              yearRangeKey={yearRangeKey}
              manifestSeries={manifestSeries}
              series={series}
              scholars={scholars}
              scholar={scholar}
              updateParam={updateParam}
            />
            <button
              onClick={() => setMobileFiltersOpen(false)}
              className="btn-primary w-full mt-6"
              data-testid="mobile-filters-apply"
            >
              Show results
            </button>
          </SheetContent>
        </Sheet>

        {/* results */}
        <div>
          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="explore-loading">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="archival-card rounded-lg p-4 flex gap-4 animate-pulse">
                  <div className="w-[92px] h-[92px] bg-white/[0.03] rounded-md" />
                  <div className="flex-1 space-y-3">
                    <div className="h-3 w-24 bg-white/[0.04] rounded" />
                    <div className="h-5 w-3/4 bg-white/[0.05] rounded" />
                    <div className="h-3 w-1/2 bg-white/[0.03] rounded" />
                  </div>
                </div>
              ))}
            </div>
          )}
          {!loading && results?.items?.length === 0 && (
            <div className="archival-card rounded-lg p-12 text-center" data-testid="explore-empty">
              <p className="font-display text-2xl text-paper">No lectures found</p>
              <p className="text-white/60 mt-2">Try a different search term or clear the filters.</p>
            </div>
          )}
          {!loading && results?.items?.length > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="explore-results">
                {results.items.map(it => <LectureCard key={it.id} item={it} />)}
              </div>
              <div className="flex items-center justify-center gap-3 mt-10">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  className="btn-ghost-gold disabled:opacity-40 disabled:cursor-not-allowed text-sm"
                  data-testid="page-prev"
                >
                  Previous
                </button>
                <span className="text-sm text-white/60">Page {page}</span>
                <button
                  disabled={(results.items.length || 0) < 24}
                  onClick={() => setPage(p => p + 1)}
                  className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed text-sm"
                  data-testid="page-next"
                >
                  Load more
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div className="mb-6">
      <p className="label-caps text-white/60 mb-3">{label}</p>
      {children}
    </div>
  );
}

function FilterPanelBody({ sort, yearRangeKey, manifestSeries, series, scholars, scholar, updateParam }) {
  return (
    <>
      <FilterGroup label="Sort">
        <select
          value={sort}
          onChange={(e) => updateParam("sort", e.target.value)}
          className="w-full bg-white/[0.04] border border-white/10 rounded-md px-3 py-2 text-sm text-paper focus:outline-none focus:border-emerald-soft/60"
          data-testid="filter-sort"
        >
          {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </FilterGroup>

      <FilterGroup label="Year">
        {YEAR_RANGES.map((r) => (
          <label key={r.key} className="flex items-center gap-3 py-2 cursor-pointer group" data-testid={`filter-year-${r.key}`}>
            <input
              type="radio"
              name="yr"
              className="accent-emerald-glow"
              checked={yearRangeKey === r.key}
              onChange={() => updateParam("yr", r.key)}
            />
            <span className="text-sm text-white/75 group-hover:text-paper">{r.label}</span>
          </label>
        ))}
      </FilterGroup>

      <FilterGroup label="Series">
        {manifestSeries.length === 0 && (
          <p className="text-xs text-white/40 italic">Loading series…</p>
        )}
        {manifestSeries.map((s) => (
          <label
            key={s.slug}
            className="flex items-center gap-3 py-2 cursor-pointer group"
            data-testid={`filter-series-${s.slug}`}
          >
            <input
              type="radio"
              name="series"
              className="accent-emerald-glow"
              checked={series === s.name}
              onChange={() => updateParam("series", series === s.name ? "" : s.name)}
            />
            <span className="text-sm text-white/75 group-hover:text-paper flex-1 truncate">{s.name}</span>
            <span className="text-[11px] text-white/40 tabular-nums">{s.live_count}</span>
          </label>
        ))}
      </FilterGroup>

      <FilterGroup label="Scholar">
        {scholars.length === 0 && (
          <p className="text-xs text-white/40 italic">No scholars yet</p>
        )}
        {scholars.map((s) => (
          <label key={s.name} className="flex items-center gap-3 py-2 cursor-pointer group" data-testid={`filter-scholar-${s.name}`}>
            <input
              type="checkbox"
              className="accent-emerald-glow w-4 h-4"
              checked={scholar === s.name}
              onChange={() => updateParam("scholar", scholar === s.name ? "" : s.name)}
            />
            <span className="text-sm text-white/75 group-hover:text-paper flex-1 truncate">{s.name}</span>
            <span className="text-[11px] text-white/40 tabular-nums">{s.count}</span>
          </label>
        ))}
      </FilterGroup>
    </>
  );
}
